var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ChartSeriesVisibility } from '@web/components/layout/RechartsItems/ChartSeriesVisibility';
import { createEdgePositionedBarTooltipContent } from '@web/components/layout/RechartsItems/EdgePositionedBarTooltip';
import LegendItem from '@web/components/layout/RechartsItems/LegendItem';
import { PeriodBarChart } from '@web/components/layout/RechartsItems/PeriodBarChart';
import { rechartsDiagonalStripeFillUrl } from '@web/components/layout/RechartsItems/rechartsDiagonalStripes';
import { ConsistencyOfAttendanceInMeetsTooltip } from '@web/components/layout/WidgetTooltips/ConsistencyOfAttendanceInMeetsTooltip';
import { buildMeetingAttendanceChartData, collectMeetingAttendanceIds, isMeetingAttendanceSeriesVisible } from '@web/components/PerformanceReview/Employee/Sections/Schedule&Attendance/meetingAttendanceByDurationChartData';
import { TablesDiffColorsConfig } from '@web/config';
import { ComparisonPeriod } from '@web/utility/chartNavigation';
import { createPeriodComparisonMatcher, formatDuration, getInterval } from '@workalizer/shared/utils';
import { normalizeMeetingAttendanceByDurationData } from '@workalizer/shared/utils/meetingAttendance';
import { useTranslation } from 'react-i18next';
var MEETING_ATTENDANCE_SERIES_KEYS = ['attended', 'missed'];
var TABLES_DATA_PREFIX = 'app.performanceReview.employeePerformanceReview.scheduleAndAttendanceSection.tablesData';
var teamStripePatternId = function (prefix, dataKey) { return "".concat(prefix, "_").concat(dataKey); };
export var MeetingAttendanceByDurationChart = function (_a) {
    var data = _a.data, chartConfig = _a.chartConfig, dateFrom = _a.dateFrom, dateTo = _a.dateTo, negativeLegendColor = _a.negativeLegendColor, teamPeriods = _a.teamPeriods, teamStripeConfig = _a.teamStripeConfig, onPeriodBarClick = _a.onPeriodBarClick;
    var t = useTranslation().t;
    var interval = getInterval(dateFrom, dateTo);
    var chartSource = normalizeMeetingAttendanceByDurationData(data);
    var showTeamBars = Boolean((teamPeriods === null || teamPeriods === void 0 ? void 0 : teamPeriods.length) && teamStripeConfig);
    var periodMatcher = createPeriodComparisonMatcher(chartSource.periods.map(function (period) { return period.period; }), chartSource.prevPeriods.map(function (period) { return period.period; }), { dateFrom: dateFrom, dateTo: dateTo, interval: interval });
    var getMatchedPrevPeriod = function (index) {
        return periodMatcher.getItem(index, chartSource.prevPeriods);
    };
    var _b = collectMeetingAttendanceIds(chartSource), uniqueMeetingIds = _b.uniqueMeetingIds, meetingAttendanceById = _b.meetingAttendanceById;
    var chartData = buildMeetingAttendanceChartData({
        chartSource: chartSource,
        getMatchedPrevPeriod: getMatchedPrevPeriod,
        teamPeriods: teamPeriods,
        showTeamBars: showTeamBars,
        minBarHeight: chartConfig.minBarHeight
    });
    var handlePeriodBarClick = function (stack) {
        if (!onPeriodBarClick) {
            return undefined;
        }
        return function (barData, index) {
            onPeriodBarClick(chartData, barData, index, stack);
        };
    };
    var tooltipColorsConfig = {
        attended: chartConfig.attended,
        missed: chartConfig.missed
    };
    return (_jsx(ChartSeriesVisibility, __assign({ seriesKeys: MEETING_ATTENDANCE_SERIES_KEYS }, { children: function (_a) {
            var toggleSeries = _a.toggleSeries, isSeriesVisible = _a.isSeriesVisible;
            return (_jsx(PeriodBarChart, { chartData: chartData, dateFrom: dateFrom, dateTo: dateTo, interval: interval, barsPerGroup: showTeamBars ? 3 : 2, yAxisTickFormatter: function (value) { return formatDuration(value, 'seconds'); }, tooltipContent: createEdgePositionedBarTooltipContent(function (_a) {
                    var _b;
                    var active = _a.active, payload = _a.payload, label = _a.label;
                    if (!active || !(payload === null || payload === void 0 ? void 0 : payload.length))
                        return null;
                    var periodIndex = chartSource.periods.findIndex(function (period) { return period.period === label; });
                    if (periodIndex === -1)
                        return null;
                    var matchedPrevPeriod = getMatchedPrevPeriod(periodIndex);
                    var teamPeriod = teamPeriods === null || teamPeriods === void 0 ? void 0 : teamPeriods[periodIndex];
                    return (_jsx(ConsistencyOfAttendanceInMeetsTooltip, { period: String(label !== null && label !== void 0 ? label : ''), previousPeriodLabel: matchedPrevPeriod === null || matchedPrevPeriod === void 0 ? void 0 : matchedPrevPeriod.period, currentMeetings: __spreadArray([], chartSource.periods[periodIndex].meetings, true).reverse(), prevMeetings: __spreadArray([], ((_b = matchedPrevPeriod === null || matchedPrevPeriod === void 0 ? void 0 : matchedPrevPeriod.meetings) !== null && _b !== void 0 ? _b : []), true).reverse(), dateFrom: dateFrom, dateTo: dateTo, colorsConfig: tooltipColorsConfig, teamDurationAverages: teamPeriod
                            ? {
                                attendedSeconds: teamPeriod.teamAverageAttended,
                                skippedSeconds: teamPeriod.teamAverageSkipped,
                                attendedStripeColor: teamStripeConfig === null || teamStripeConfig === void 0 ? void 0 : teamStripeConfig.attendedColor,
                                skippedStripeColor: teamStripeConfig === null || teamStripeConfig === void 0 ? void 0 : teamStripeConfig.skippedColor
                            }
                            : undefined }));
                }, chartData.length), legend: MEETING_ATTENDANCE_SERIES_KEYS.map(function (key) {
                    var config = chartConfig[key];
                    var count = key === 'attended'
                        ? chartSource.totals.attendedCount
                        : chartSource.totals.missedCount;
                    var duration = key === 'attended'
                        ? chartSource.totals.attendedDuration
                        : chartSource.totals.missedDuration;
                    var diffValue = key === 'attended'
                        ? chartSource.totals.attendedDurationPrevPeriodDiff
                        : chartSource.totals.missedDurationPrevPeriodDiff;
                    var teamAverage = key === 'attended'
                        ? chartSource.totals.attendedDurationTeamAvgDiff
                        : chartSource.totals.missedDurationTeamAvgDiff;
                    return (_jsx(LegendItem, { color: key === 'missed'
                            ? (negativeLegendColor !== null && negativeLegendColor !== void 0 ? negativeLegendColor : config.color)
                            : config.color, label: t("".concat(TABLES_DATA_PREFIX, ".").concat(config.translationKey)), currentValueClassName: "inline-flex items-baseline gap-0 text-sm", currentValue: _jsxs(_Fragment, { children: [_jsx("span", __assign({ className: "font-semibold" }, { children: count })), _jsx("span", __assign({ className: "font-normal text-gray-500" }, { children: "\u00A0/\u00A0" })), _jsx("span", __assign({ className: "font-semibold" }, { children: formatDuration(duration, 'seconds') }))] }), diffValue: diffValue, teamAverage: showTeamBars ? (teamAverage !== null && teamAverage !== void 0 ? teamAverage : undefined) : undefined, arrowColor: TablesDiffColorsConfig.GREEN, stripeChartColor: showTeamBars
                            ? key === 'attended'
                                ? teamStripeConfig === null || teamStripeConfig === void 0 ? void 0 : teamStripeConfig.attendedColor
                                : teamStripeConfig === null || teamStripeConfig === void 0 ? void 0 : teamStripeConfig.skippedColor
                            : undefined, onClick: function () { return toggleSeries(key); }, isHidden: !isSeriesVisible(key) }, key));
                }), stripePatterns: showTeamBars && teamStripeConfig
                    ? [
                        {
                            patternId: teamStripePatternId(teamStripeConfig.patternIdPrefix, 'teamAverageAttended'),
                            chartColor: teamStripeConfig.attendedColor
                        },
                        {
                            patternId: teamStripePatternId(teamStripeConfig.patternIdPrefix, 'teamAverageSkipped'),
                            chartColor: teamStripeConfig.skippedColor
                        }
                    ]
                    : undefined, stacks: __spreadArray([
                    {
                        stackId: 'prev',
                        segments: uniqueMeetingIds.map(function (meetingId) { return ({
                            dataKey: "prev_meeting_".concat(meetingId),
                            fill: chartConfig.attended.prevChartColor,
                            hide: !isMeetingAttendanceSeriesVisible(meetingAttendanceById, meetingId, isSeriesVisible),
                            keyPrefix: "prev-".concat(meetingId),
                            getCellProps: function (entry) {
                                var attendance = entry["prev_meeting_".concat(meetingId, "_attendance")];
                                return {
                                    fill: attendance
                                        ? chartConfig.attended.prevChartColor
                                        : chartConfig.missed.prevChartColor,
                                    stroke: chartConfig.missed.prevStrokeColor,
                                    strokeWidth: 1
                                };
                            },
                            onClick: handlePeriodBarClick(ComparisonPeriod.Previous)
                        }); })
                    },
                    {
                        stackId: 'curr',
                        segments: uniqueMeetingIds.map(function (meetingId) { return ({
                            dataKey: "meeting_".concat(meetingId),
                            fill: chartConfig.attended.chartColor,
                            hide: !isMeetingAttendanceSeriesVisible(meetingAttendanceById, meetingId, isSeriesVisible),
                            keyPrefix: "curr-".concat(meetingId),
                            getCellProps: function (entry) {
                                var attendance = entry["meeting_".concat(meetingId, "_attendance")];
                                return {
                                    fill: attendance
                                        ? chartConfig.attended.chartColor
                                        : chartConfig.missed.chartColor,
                                    stroke: chartConfig.missed.strokeColor,
                                    strokeWidth: 1
                                };
                            },
                            onClick: handlePeriodBarClick(ComparisonPeriod.Current)
                        }); })
                    }
                ], (showTeamBars && teamStripeConfig
                    ? [
                        {
                            stackId: 'team',
                            segments: [
                                {
                                    dataKey: 'teamAverageAttended',
                                    fill: rechartsDiagonalStripeFillUrl(teamStripePatternId(teamStripeConfig.patternIdPrefix, 'teamAverageAttended')),
                                    hide: !isSeriesVisible('attended'),
                                    keyPrefix: 'team-attended',
                                    onClick: handlePeriodBarClick(ComparisonPeriod.Current)
                                },
                                {
                                    dataKey: 'teamAverageSkipped',
                                    fill: rechartsDiagonalStripeFillUrl(teamStripePatternId(teamStripeConfig.patternIdPrefix, 'teamAverageSkipped')),
                                    hide: !isSeriesVisible('missed'),
                                    keyPrefix: 'team-skipped',
                                    onClick: handlePeriodBarClick(ComparisonPeriod.Current)
                                }
                            ]
                        }
                    ]
                    : []), true) }));
        } })));
};
